import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useContent } from "@/lib/content";
import { DOMAIN } from "@/lib/constants";
import PageHero from "@/components/site/PageHero";
import { inputCls, labelCls, submitLead, SuccessPanel } from "@/components/site/leadForm";

const EMPREENDIMENTOS = [
  "Posto de combustível / eletroposto",
  "Empresa / frota",
  "Supermercado / centro comercial",
  "Hotel / pousada",
  "Estacionamento",
  "Condomínio",
  "Rodovia / ponto estratégico",
  "Residência",
  "Outro",
];

const POTENCIAS = [
  "Ainda não sei — preciso de orientação",
  "AC (destino / pernoite)",
  "DC — até 60 kW",
  "DC — 60 a 180 kW",
  "DC — acima de 180 kW",
];

const QUANTIDADES = ["1", "2", "3 a 5", "6 ou mais"];

export default function ContatoPage() {
  useSeo(
    "Contato | Solicite Orçamento de Carregadores e Energia Solar — HB Solar",
    "Solicite orçamento de carregadores para veículos elétricos, eletropostos e energia solar. Atendimento comercial HB Solar / HB Charger."
  );

  const [params] = useSearchParams();
  const { company: COMPANY } = useContent();
  const [form, setForm] = useState({
    nome: "", empresa: "", cidade: "", telefone: "",
    tipo_empreendimento: EMPREENDIMENTOS[0], potencia_interesse: POTENCIAS[0],
    quantidade: QUANTIDADES[0], mensagem: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [whatsMsg, setWhatsMsg] = useState("");

  useEffect(() => {
    const produto = params.get("produto");
    const assunto = params.get("assunto");
    if (produto) {
      setForm((f) => ({ ...f, mensagem: `Tenho interesse no ${produto}.` }));
    } else if (assunto === "plataforma") {
      setForm((f) => ({ ...f, mensagem: "Quero conhecer as condições comerciais da plataforma de gerenciamento." }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefone.trim() || !form.cidade.trim()) {
      toast.error("Preencha nome, WhatsApp e cidade/UF.");
      return;
    }
    const payload = {
      tipo: "contato",
      nome: form.nome.trim(),
      telefone: form.telefone.trim(),
      cidade: form.cidade.trim(),
      empresa: form.empresa.trim() || null,
      tipo_empreendimento: form.tipo_empreendimento,
      potencia_interesse: form.potencia_interesse,
      quantidade: form.quantidade,
      mensagem: form.mensagem.trim() || null,
    };
    const msg =
      `Olá HB Solar! Solicito contato comercial.\n\n*Nome:* ${payload.nome}\n*Empresa:* ${payload.empresa || "—"}\n*Cidade/UF:* ${payload.cidade}\n*WhatsApp:* ${payload.telefone}\n*Empreendimento:* ${payload.tipo_empreendimento}\n*Potência de interesse:* ${payload.potencia_interesse}\n*Quantidade:* ${payload.quantidade}\n*Mensagem:* ${payload.mensagem || "—"}\n\nVim pelo site ${DOMAIN}`;
    setWhatsMsg(msg);
    setSending(true);
    const ok = await submitLead(payload, msg);
    setSending(false);
    if (ok) {
      toast.success("Solicitação registrada! Conclua o envio no WhatsApp que abriu.");
    } else {
      toast.error("Não conseguimos registrar agora — conclua pelo WhatsApp que abriu.");
    }
    setSent(true);
  };

  const reset = () => {
    setForm({ nome: "", empresa: "", cidade: "", telefone: "", tipo_empreendimento: EMPREENDIMENTOS[0], potencia_interesse: POTENCIAS[0], quantidade: QUANTIDADES[0], mensagem: "" });
    setSent(false);
    setWhatsMsg("");
  };

  return (
    <>
      <PageHero
        testId="contact-page-title"
        eyebrow="Contato"
        title="Solicite seu orçamento"
        desc="Preencha os dados abaixo e sua solicitação segue direto para nosso atendimento comercial — por e-mail e WhatsApp."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div className="rounded-3xl bg-[#0B192C] p-8 sm:p-10 lg:sticky lg:top-28">
            <h2 className="font-display text-xl font-bold text-white">Atendimento comercial</h2>
            <ul className="mt-6 space-y-4 text-sm text-slate-300">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-amber-400" /> {COMPANY.telefone}
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-amber-400" /> {COMPANY.email}
              </li>
            </ul>
            <p className="mt-6 text-xs leading-relaxed text-slate-500">
              Sua solicitação é registrada em nosso sistema, enviada por e-mail à
              equipe comercial e encaminhada ao nosso WhatsApp. Respondemos em
              horário comercial.
            </p>
          </div>

          {sent ? (
            <SuccessPanel
              title="Solicitação registrada com sucesso!"
              text="Nosso comercial já recebeu seus dados por e-mail. Para agilizar, conclua o envio da mensagem que abriu no seu WhatsApp."
              whatsMsg={whatsMsg}
              onReset={reset}
            />
          ) : (
            <form data-testid="contact-form" onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-nome" className={labelCls}>Nome *</label>
                  <input id="c-nome" data-testid="contact-input-name" className={inputCls} value={form.nome} onChange={set("nome")} placeholder="Seu nome" required />
                </div>
                <div>
                  <label htmlFor="c-empresa" className={labelCls}>Empresa</label>
                  <input id="c-empresa" data-testid="contact-input-company" className={inputCls} value={form.empresa} onChange={set("empresa")} placeholder="Nome da empresa (opcional)" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-cidade" className={labelCls}>Cidade e Estado *</label>
                  <input id="c-cidade" data-testid="contact-input-city" className={inputCls} value={form.cidade} onChange={set("cidade")} placeholder="Ex: Ilhéus — BA" required />
                </div>
                <div>
                  <label htmlFor="c-tel" className={labelCls}>WhatsApp *</label>
                  <input id="c-tel" data-testid="contact-input-phone" type="tel" className={inputCls} value={form.telefone} onChange={set("telefone")} placeholder="(73) 99999-9999" required />
                </div>
              </div>
              <div>
                <label htmlFor="c-tipo" className={labelCls}>Tipo de empreendimento</label>
                <select id="c-tipo" data-testid="contact-select-business" className={inputCls} value={form.tipo_empreendimento} onChange={set("tipo_empreendimento")}>
                  {EMPREENDIMENTOS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-pot" className={labelCls}>Potência de interesse</label>
                  <select id="c-pot" data-testid="contact-select-power" className={inputCls} value={form.potencia_interesse} onChange={set("potencia_interesse")}>
                    {POTENCIAS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="c-qtd" className={labelCls}>Quantidade de carregadores</label>
                  <select id="c-qtd" data-testid="contact-select-qty" className={inputCls} value={form.quantidade} onChange={set("quantidade")}>
                    {QUANTIDADES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="c-msg" className={labelCls}>Mensagem</label>
                <textarea id="c-msg" data-testid="contact-input-message" rows={4} className={inputCls} value={form.mensagem} onChange={set("mensagem")} placeholder="Conte um pouco sobre seu projeto (opcional)" />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#25D366] hover:bg-[#1fb857] text-white font-extrabold text-base px-7 py-4 transition-colors duration-300 shadow-[0_16px_40px_rgba(37,211,102,0.3)] disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Enviando..." : "Enviar solicitação"}
              </button>
              <p className="text-center text-xs text-slate-400 font-medium">
                Ao enviar, o WhatsApp abre com a mensagem pronta para a HB Solar — e seus dados também chegam por e-mail.
              </p>
            </form>
          )}
        </div>

        <div data-testid="contact-map" className="max-w-6xl mx-auto px-5 sm:px-8 mt-12">
          <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 px-8 py-5 border-b border-slate-100">
              <MapPin className="h-5 w-5 text-amber-500" />
              <div>
                <h2 className="font-display text-lg font-bold text-[#0B192C]">Onde estamos</h2>
                <p className="text-xs font-semibold text-slate-500">Jequié — Bahia · atendemos toda a região</p>
              </div>
            </div>
            <iframe
              title="Localização da HB Solar — Jequié, Bahia"
              src="https://www.google.com/maps?q=Jequi%C3%A9%2C%20Bahia%2C%20Brasil&output=embed"
              className="h-80 w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
    </section>
  </>
  );
}
